import { useCallback, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { selectUseEventsManagerDashboard } from 'src/v3/settings/settings.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { activeEventChanged } from 'src/v3/events/events.slice'
import tabLabel from 'src/v3/events/v2/components/tab-label'
import { selectActiveLinkedService } from '../manage.slice'
import { DashboardQueryProvider } from '../dashboard/query-provider'
import {
  fetchManageEventCategories,
  fetchManageEvents,
  filtersChanged,
  indexTabChanged,
  isUnscheduledEvent,
  linkedServiceFilterSeeded,
  selectManageEventCategoriesStatus,
  selectManageEventsFilters,
  selectManageEventsIndexTab,
  selectManageEventsLinkedServiceSeeded,
  selectManageEventsStatus,
  selectScheduledManageEvents,
  selectServiceScopedManageEvents,
  selectUnscheduledManageEvents,
} from './manage-events.slice'
import type {
  ManageEventsFilters,
  ManageEventsIndexTab,
} from './manage-events.slice'
import EventsKpiCards from './components/events-kpi-cards'
import EventsFilters from './components/events-filters'
import EventsTable from './components/events-table'

let emptyMessages: Record<ManageEventsIndexTab, string> = {
  scheduled: 'No events are scheduled',
  unscheduled: 'Every event has an upcoming session',
}

let ManageEventsContent = () => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let serviceEvents = useAppSelector(selectServiceScopedManageEvents)
  let scheduledEvents = useAppSelector(selectScheduledManageEvents)
  let unscheduledEvents = useAppSelector(selectUnscheduledManageEvents)
  let tab = useAppSelector(selectManageEventsIndexTab)
  let eventsStatus = useAppSelector(selectManageEventsStatus)
  let categoriesStatus = useAppSelector(selectManageEventCategoriesStatus)
  let filters = useAppSelector(selectManageEventsFilters)
  let linkedServiceSeeded = useAppSelector(
    selectManageEventsLinkedServiceSeeded,
  )
  let activeLinkedService = useAppSelector(selectActiveLinkedService)

  useScrollToTop()

  useAutoFetch(eventsStatus, fetchManageEvents)
  useAutoFetch(categoriesStatus, fetchManageEventCategories)

  useEffect(() => {
    if (
      !linkedServiceSeeded &&
      activeLinkedService &&
      activeLinkedService !== 'unselected'
    ) {
      dispatch(filtersChanged({ linkedServiceID: activeLinkedService }))
      dispatch(linkedServiceFilterSeeded())
    }
  }, [activeLinkedService, dispatch, linkedServiceSeeded])

  let handleFiltersChange = useCallback(
    (partial: Partial<ManageEventsFilters>) => {
      dispatch(filtersChanged(partial))
    },
    [dispatch],
  )

  let loading = eventsStatus === 'pending' || eventsStatus === 'uninitialized'
  let countOrDash = (n: number): number | string => (loading ? '-' : n)

  let tabEvents = tab === 'scheduled' ? scheduledEvents : unscheduledEvents
  // Distinguish "no events at all", "nothing in this tab", and "filters hid everything".
  let tabHasAnyEvents = serviceEvents.some(
    (e) => isUnscheduledEvent(e) === (tab === 'unscheduled'),
  )
  let emptyMessage =
    serviceEvents.length === 0
      ? 'No events yet'
      : !tabHasAnyEvents
        ? emptyMessages[tab]
        : 'No events match your filters'

  return (
    <Box>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box className="flex flex-col gap-6">
          <Box className="flex items-center justify-between">
            <Typography variant="h5" component="h1">
              Events
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                dispatch(activeEventChanged('unselected'))
                navigate('/events/create')
              }}
            >
              Create event
            </Button>
          </Box>
          <EventsKpiCards
            scheduledEvents={scheduledEvents}
            unscheduledCount={unscheduledEvents.length}
            loading={loading}
          />
          <Tabs
            value={tab}
            onChange={(_, value: ManageEventsIndexTab) =>
              dispatch(indexTabChanged(value))
            }
            aria-label="Event schedule tabs"
          >
            <Tab
              id="manage-events-tab-scheduled"
              aria-controls="manage-events-tabpanel"
              value="scheduled"
              label={tabLabel(
                'Scheduled',
                countOrDash(scheduledEvents.length),
                'text.secondary',
              )}
            />
            <Tab
              id="manage-events-tab-unscheduled"
              aria-controls="manage-events-tabpanel"
              value="unscheduled"
              label={tabLabel(
                'Unscheduled',
                countOrDash(unscheduledEvents.length),
                'text.secondary',
              )}
            />
          </Tabs>
          <Box
            role="tabpanel"
            id="manage-events-tabpanel"
            aria-labelledby={`manage-events-tab-${tab}`}
            className="flex flex-col gap-6"
          >
            <EventsFilters
              filters={filters}
              tab={tab}
              onChange={handleFiltersChange}
            />
            {/* Keyed by tab so pagination and sort reset when switching. */}
            <EventsTable
              key={tab}
              events={tabEvents}
              loading={loading}
              tab={tab}
              emptyMessage={emptyMessage}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

let ManageEventsPage = () => {
  let useEventsManagerDashboard = useAppSelector(
    selectUseEventsManagerDashboard,
  )

  if (!useEventsManagerDashboard) {
    return <Navigate to="/manage" replace />
  }

  return (
    <DashboardQueryProvider>
      <ManageEventsContent />
    </DashboardQueryProvider>
  )
}

export default ManageEventsPage
